import {
  Visita,
  VisitaAgendaComprador,
  VisitaFechamento,
  VisitaSlot,
  VisitaHistoricoStatus,
  VisitaComprovante,
  VisitaIndicadores,
  Usuario
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const FRONTEND_SECRET = process.env.REACT_APP_FRONTEND_SECRET;

const getAuthToken = (): string | null => localStorage.getItem('@sistema-up:token');

const apiRequest = async (path: string, options: RequestInit = {}): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(FRONTEND_SECRET ? { 'X-Frontend-Secret': FRONTEND_SECRET } : {}),
      ...(options.headers || {})
    }
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Erro na API de visitas');
  return result;
};

// ===================== AGENDA DO COMPRADOR =====================

export const VisitasService = {
  // Buscar compradores disponíveis
  async buscarCompradores(filial?: string): Promise<{ data: Usuario[]; error?: any }> {
    try {
      const params = filial ? `?filial=${filial}` : '';
      const result = await apiRequest(`/visitas/compradores${params}`);
      return { data: result.data || [], error: null };
    } catch (error) {
      console.error('Erro ao buscar compradores:', error);
      return { data: [], error };
    }
  },

  // Buscar agenda de um comprador
  async buscarAgenda(compradorId: string): Promise<VisitaAgendaComprador[]> {
    try {
      const result = await apiRequest(`/visitas/agenda/${compradorId}`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar agenda:', error);
      return [];
    }
  },

  // Salvar agenda de um comprador
  async salvarAgenda(compradorId: string, configuracoes: Partial<VisitaAgendaComprador>[]): Promise<{ data?: VisitaAgendaComprador[]; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/agenda/${compradorId}`, {
        method: 'POST',
        body: JSON.stringify({ configuracoes })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao salvar agenda:', error);
      return { error };
    }
  },

  // ===================== FECHAMENTOS =====================

  async buscarFechamentos(compradorId: string, dataInicio?: string, dataFim?: string): Promise<VisitaFechamento[]> {
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.set('data_inicio', dataInicio);
      if (dataFim) params.set('data_fim', dataFim);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const result = await apiRequest(`/visitas/fechamentos/${compradorId}${queryString}`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar fechamentos:', error);
      return [];
    }
  },

  async fecharDia(fechamento: Partial<VisitaFechamento>): Promise<{ data?: VisitaFechamento; error?: any }> {
    try {
      const result = await apiRequest('/visitas/fechamentos', {
        method: 'POST',
        body: JSON.stringify(fechamento)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao fechar dia:', error);
      return { error };
    }
  },

  async reabrirDia(id: string): Promise<{ data?: VisitaFechamento; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/fechamentos/${id}`, {
        method: 'DELETE'
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao reabrir dia:', error);
      return { error };
    }
  },

  // ===================== SLOTS ESPECÍFICOS POR DIA =====================

  async buscarSlotsDia(compradorId: string, data: string): Promise<{ inicio: string; fim: string; fechado: boolean; motivo: string }[]> {
    try {
      const result = await apiRequest(`/visitas/slots-dia?comprador_id=${compradorId}&data=${data}`);
      return (result || []).map((s: any) => ({
        inicio: s.horario_inicio?.substring(0, 5) || s.inicio,
        fim: s.horario_fim?.substring(0, 5) || s.fim,
        fechado: s.fechado || false,
        motivo: s.motivo || ''
      }));
    } catch {
      return [];
    }
  },

  async salvarSlotsDia(compradorId: string, data: string, slots: { inicio: string; fim: string }[]): Promise<{ error?: any }> {
    try {
      await apiRequest('/visitas/slots-dia', {
        method: 'POST',
        body: JSON.stringify({ comprador_id: compradorId, data, slots })
      });
      return {};
    } catch (error) {
      return { error };
    }
  },

  // ===================== DISPONIBILIDADE =====================

  async buscarDisponibilidade(compradorId: string, data: string): Promise<{ data: VisitaSlot[]; fechado: boolean; motivo?: string }> {
    try {
      const result = await apiRequest(`/visitas/disponibilidade/${compradorId}?data=${data}`);
      return {
        data: result.data || [],
        fechado: result.fechado || false,
        motivo: result.motivo
      };
    } catch (error) {
      console.error('Erro ao buscar disponibilidade:', error);
      return { data: [], fechado: true, motivo: 'Erro ao carregar disponibilidade' };
    }
  },

  // ===================== VISITAS =====================

  async criarVisita(visita: Partial<Visita>): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest('/visitas', {
        method: 'POST',
        body: JSON.stringify(visita)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao criar visita:', error);
      return { error };
    }
  },

  async buscarVisitas(filtros?: {
    comprador_id?: string;
    solicitante_id?: string;
    status?: string[];
    data_inicio?: string;
    data_fim?: string;
  }): Promise<Visita[]> {
    try {
      const params = new URLSearchParams();
      if (filtros?.comprador_id) params.set('comprador_id', filtros.comprador_id);
      if (filtros?.solicitante_id) params.set('solicitante_id', filtros.solicitante_id);
      if (filtros?.status) params.set('status', filtros.status.join(','));
      if (filtros?.data_inicio) params.set('data_inicio', filtros.data_inicio);
      if (filtros?.data_fim) params.set('data_fim', filtros.data_fim);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const result = await apiRequest(`/visitas${queryString}`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar visitas:', error);
      return [];
    }
  },

  async buscarVisitaPorId(id: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}`);
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao buscar visita:', error);
      return { error };
    }
  },

  async atualizarVisita(id: string, updates: Partial<Visita>): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao atualizar visita:', error);
      return { error };
    }
  },

  async aprovarVisita(id: string, observacoes?: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/aprovar`, {
        method: 'POST',
        body: JSON.stringify({ observacoes })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao aprovar visita:', error);
      return { error };
    }
  },

  async reprovarVisita(id: string, motivo: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/reprovar`, {
        method: 'POST',
        body: JSON.stringify({ motivo })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao reprovar visita:', error);
      return { error };
    }
  },

  async reagendarVisita(id: string, data: {
    data_visita: string;
    horario_inicio: string;
    horario_fim: string;
    motivo?: string;
  }): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/reagendar`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao reagendar visita:', error);
      return { error };
    }
  },

  async cancelarVisita(id: string, motivo?: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/cancelar`, {
        method: 'POST',
        body: JSON.stringify({ motivo })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao cancelar visita:', error);
      return { error };
    }
  },

  // ===================== CHECKIN E ATENDIMENTO =====================

  async realizarCheckin(id: string, dados: {
    documento_confirmado?: string;
    empresa_confirmada?: string;
    nome_recepcionista?: string;
    observacoes?: string;
  }): Promise<{ data?: { visita: Visita; checkin: any }; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/checkin`, {
        method: 'POST',
        body: JSON.stringify(dados)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao realizar checkin:', error);
      return { error };
    }
  },

  async iniciarAtendimento(id: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/iniciar-atendimento`, {
        method: 'POST'
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao iniciar atendimento:', error);
      return { error };
    }
  },

  async finalizarAtendimento(id: string, observacoes?: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/finalizar-atendimento`, {
        method: 'POST',
        body: JSON.stringify({ observacoes })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao finalizar atendimento:', error);
      return { error };
    }
  },

  async buscarHistorico(id: string): Promise<VisitaHistoricoStatus[]> {
    try {
      const result = await apiRequest(`/visitas/${id}/historico`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      return [];
    }
  },

  // ===================== INDICADORES E COMPROVANTE =====================

  async buscarIndicadores(filtros?: {
    comprador_id?: string;
    data_inicio?: string;
    data_fim?: string;
    fornecedor?: string;
  }): Promise<VisitaIndicadores> {
    try {
      const params = new URLSearchParams();
      if (filtros?.comprador_id) params.set('comprador_id', filtros.comprador_id);
      if (filtros?.data_inicio) params.set('data_inicio', filtros.data_inicio);
      if (filtros?.data_fim) params.set('data_fim', filtros.data_fim);
      if (filtros?.fornecedor) params.set('fornecedor', filtros.fornecedor);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const result = await apiRequest(`/visitas/indicadores/resumo${queryString}`);
      return result.data;
    } catch (error) {
      console.error('Erro ao buscar indicadores:', error);
      return {
        por_status: {},
        por_status_percentual: {},
        total_agendamentos: 0,
        agendamentos_realizados: 0,
        agendamentos_aprovados: 0,
        taxa_aprovacao: 0,
        taxa_recusa: 0,
        taxa_cancelamento: 0,
        taxa_reagendamento: 0,
        tempo_medio_aprovacao_minutos: 0,
        tempo_medio_espera_minutos: 0,
        tempo_medio_atendimento_minutos: 0,
        tempo_medio_permanencia_minutos: 0,
        tempo_medio_liberacao_recepcao_minutos: 0,
        tempo_medio_solicitacao_visita_dias: 0,
        pontualidade_visitantes: 0,
        comparecimento: 0,
        faltas_no_show: 0,
        indice_atendimento_concluido: 0,
        comprador_maior_volume: null,
        fornecedor_mais_atendido: null,
        representante_mais_visitas: null,
        top_compradores: [],
        top_fornecedores: [],
        top_representantes: [],
        piores_fornecedores: [],
        horarios_mais_utilizados: [],
        evolucao_diaria: [],
        taxa_ocupacao_agenda: 0,
        total_finalizadas: 0,
        visitas_hoje: 0
      };
    }
  },

  async gerarComprovante(id: string): Promise<{ data?: VisitaComprovante; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/comprovante`);
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao gerar comprovante:', error);
      return { error };
    }
  },

  // ===================== TRANSFERÊNCIA =====================

  async transferirVisita(id: string, data: {
    nova_data_visita: string;
    novo_horario_inicio: string;
    novo_horario_fim?: string;
    motivo_transferencia_id?: string;
    motivo_transferencia_texto: string;
    observacao_transferencia?: string;
  }): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/transferir`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao transferir visita:', error);
      return { error };
    }
  },

  // ===================== NÃO COMPARECEU =====================

  async registrarNaoCompareceu(id: string, observacao?: string): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/nao-compareceu`, {
        method: 'POST',
        body: JSON.stringify({ observacao })
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao registrar não comparecimento:', error);
      return { error };
    }
  },

  // ===================== CANCELAR COM MOTIVO =====================

  async cancelarComMotivo(id: string, data: {
    motivo_cancelamento_id?: string;
    motivo_cancelamento_texto: string;
    observacao_cancelamento?: string;
  }): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/cancelar-com-motivo`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao cancelar visita com motivo:', error);
      return { error };
    }
  },

  // ===================== FINALIZAR COM MOTIVO + AVALIAÇÃO =====================

  async finalizarComMotivo(id: string, data: {
    motivo_finalizacao_id?: string;
    motivo_finalizacao_texto: string;
    observacao_finalizacao?: string;
    avaliacao_respostas?: { pergunta: string; nota: number }[];
    avaliacao_observacao?: string;
  }): Promise<{ data?: Visita; error?: any }> {
    try {
      const result = await apiRequest(`/visitas/${id}/finalizar-com-motivo`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao finalizar atendimento com motivo:', error);
      return { error };
    }
  }
};
